// Process maps: one process as swimlanes, steps and handoffs, with its scenarios (the paths through it). Shared by
// the API, which releases maps and hands people and agents the steps to follow, and the web app, which draws and edits
// them, so both read a map the same way. Pure functions only: no database, no DOM.
//
// A map has a draft (what people are editing) and a released snapshot (the revision in effect). A lane is who does
// the work: a role, or an AI agent. People do the steps in their lanes and agents do the steps in agent lanes, both
// from the revision in effect, never the draft. A scenario is a path through the map, chosen at each decision.
// Releasing is the one approval: a person releases the next revision, and it is filed in document control.

import { LIBRARY, buildTemplate } from "./templates";

export type SystemKind = "courage" | "bc" | "manual";
export type StepType = "start" | "task" | "decision" | "end";
export type StepLink = { id: string; label: string; url: string };
/** A lane is who does the work. An agent lane is done by an AI agent, through CourageMFG's tools. */
export type Lane = { id: string; name: string; agent?: boolean };
export type Step = {
  id: string; name: string; type: StepType; lane: string;
  system: SystemKind; screen: string; owner: string;
  instruction: string; kpi: string; control: string;
  link?: StepLink | null;
};
export type Flow = { from: string; to: string; label: string };
export type Scenario = { id: string; name: string; choices: Record<string, string>; condition: string };
export type MapBody = { lanes: Lane[]; steps: Step[]; flows: Flow[]; scenarios: Scenario[] };
/** A map without its scenarios: what step paths are walked on. */
export type Snapshot = { lanes: Lane[]; steps: Step[]; flows: Flow[] };
/** The revision in effect: the map and its scenarios as released. */
export type Released = Snapshot & { scenarios: Scenario[] };

/** The fields a change is reported on, in the order people read a step. */
export const FIELDS = ["name", "type", "lane", "system", "screen", "instruction", "owner", "control", "kpi", "link"] as const;
export const FIELD_LABEL: Record<string, string> = {
  name: "Name", type: "Type", lane: "Lane", system: "System", screen: "Screen", instruction: "Instruction",
  owner: "Owner", control: "Control", kpi: "KPI", link: "Linked instruction", next: "Next step",
};
export const SYSTEM_LABEL: Record<SystemKind, string> = { courage: "Courage", bc: "BC", manual: "Manual" };

const val = (x: unknown) => (x && typeof x === "object" ? JSON.stringify(x) : String(x ?? ""));

export function byId(m: { steps: Step[] }): Record<string, Step> {
  const o: Record<string, Step> = {};
  for (const s of m.steps) o[s.id] = s;
  return o;
}
export const outs = (m: { flows: Flow[] }, id: string) => m.flows.filter((f) => f.from === id);
export const ins = (m: { flows: Flow[] }, id: string) => m.flows.filter((f) => f.to === id);
export function startId(m: { steps: Step[] }): string | undefined {
  return m.steps.find((s) => s.type === "start")?.id ?? m.steps[0]?.id;
}

/** The steps a scenario walks through, from the start, taking its choice at each decision (the first branch if none). */
export function pathOf(m: { steps: Step[]; flows: Flow[] }, sc: Pick<Scenario, "choices">): string[] {
  const B = byId(m), path: string[] = [], seen = new Set<string>();
  let cur = startId(m);
  while (cur && B[cur] && !seen.has(cur)) {
    seen.add(cur); path.push(cur);
    const o = outs(m, cur);
    if (!o.length) break;
    let next = o[0].to;
    if (o.length > 1) { const pick = o.find((f) => f.to === sc.choices?.[cur!]); if (pick) next = pick.to; }
    cur = next;
  }
  return path;
}

/**
 * After a change to the map, keep each scenario on the path it was on: at a decision with no choice (a new one, or
 * one whose chosen branch is gone), take the branch that leads back to the old path.
 */
export function fixChoices(prev: MapBody, next: MapBody): void {
  const B = byId(next);
  for (const sc of next.scenarios) {
    const old = prev.scenarios.find((s) => s.id === sc.id) ?? sc;
    const before = new Set(pathOf(prev, old));
    const kept: Record<string, string> = {};
    for (const [k, v] of Object.entries(sc.choices ?? {})) if (B[k] && outs(next, k).some((f) => f.to === v)) kept[k] = v;
    const seen = new Set<string>();
    let cur = startId(next);
    while (cur && B[cur] && !seen.has(cur)) {
      seen.add(cur);
      const o = outs(next, cur);
      if (!o.length) break;
      let nx = o[0].to;
      if (o.length > 1) {
        const f = o.find((x) => x.to === kept[cur!]) ?? o.find((x) => before.has(x.to)) ?? o[0];
        kept[cur] = f.to; nx = f.to;
      }
      cur = nx;
    }
    sc.choices = kept;
  }
}

export type Diff = {
  added: string[]; removed: Step[];
  changed: Record<string, string[]>; // step id → what changed on it (FIELDS, and "next")
  lanes: boolean;
  scenarios: { added: string[]; removed: Scenario[]; changed: string[] }; // changed: renamed, or takes another branch
  count: number;
};

/** What changed in the draft since the released revision. With nothing released yet, every step is new. */
export function diffMaps(draft: Snapshot & { scenarios?: Scenario[] }, released: (Snapshot & { scenarios?: Scenario[] }) | null): Diff {
  const out: Diff = { added: [], removed: [], changed: {}, lanes: false, scenarios: { added: [], removed: [], changed: [] }, count: 0 };
  if (!released) {
    out.added = draft.steps.map((s) => s.id); out.scenarios.added = (draft.scenarios ?? []).map((s) => s.id);
    out.count = out.added.length + out.scenarios.added.length; return out;
  }
  const R = byId(released), C = byId(draft);
  const sig = (fl: Flow[], id: string) => fl.filter((x) => x.from === id).map((x) => `${x.to}:${x.label ?? ""}`).sort().join(",");
  for (const s of draft.steps) {
    const r = R[s.id];
    if (!r) { out.added.push(s.id); continue; }
    const f: string[] = FIELDS.filter((k) => val((r as any)[k]) !== val((s as any)[k]));
    if (sig(released.flows, s.id) !== sig(draft.flows, s.id)) f.push("next");
    if (f.length) out.changed[s.id] = f;
  }
  for (const r of released.steps) if (!C[r.id]) out.removed.push(r);
  const lanesOf = (ls: Lane[]) => JSON.stringify(ls.map((l) => [l.id, l.name, !!l.agent]));
  out.lanes = lanesOf(draft.lanes) !== lanesOf(released.lanes);
  if (draft.scenarios && released.scenarios) {
    const RS = new Map(released.scenarios.map((s) => [s.id, s]));
    for (const sc of draft.scenarios) {
      const r = RS.get(sc.id);
      if (!r) out.scenarios.added.push(sc.id);
      else if (r.name !== sc.name || r.condition !== sc.condition || pathOf(draft, sc).join() !== pathOf(draft, r).join()) out.scenarios.changed.push(sc.id);
    }
    const DS = new Set(draft.scenarios.map((s) => s.id));
    out.scenarios.removed = released.scenarios.filter((s) => !DS.has(s.id));
  }
  const sc = out.scenarios;
  out.count = out.added.length + out.removed.length + Object.keys(out.changed).length + (out.lanes ? 1 : 0)
    + sc.added.length + sc.removed.length + sc.changed.length;
  return out;
}

export function nextRev(rev: string | null | undefined): string {
  if (!rev) return "A";
  const last = rev.charCodeAt(rev.length - 1);
  return last >= 90 ? rev + "A" : rev.slice(0, -1) + String.fromCharCode(last + 1);
}

export function summaryOf(diff: Diff, draft: Snapshot & { scenarios?: Scenario[] }): string {
  const B = byId(draft), parts: string[] = [];
  const names = (ids: string[]) => ids.map((id) => B[id]?.name ?? id).join(", ");
  const scName = (id: string) => draft.scenarios?.find((s) => s.id === id)?.name ?? id;
  if (diff.added.length) parts.push(`Added ${names(diff.added)}`);
  if (Object.keys(diff.changed).length) parts.push(`Changed ${names(Object.keys(diff.changed))}`);
  if (diff.removed.length) parts.push(`Removed ${diff.removed.map((s) => s.name).join(", ")}`);
  if (diff.lanes) parts.push("Lanes updated");
  if (diff.scenarios.added.length) parts.push(`New scenario${diff.scenarios.added.length > 1 ? "s" : ""} ${diff.scenarios.added.map(scName).join(", ")}`);
  if (diff.scenarios.changed.length) parts.push(`Scenario${diff.scenarios.changed.length > 1 ? "s" : ""} ${diff.scenarios.changed.map(scName).join(", ")} updated`);
  if (diff.scenarios.removed.length) parts.push(`Retired ${diff.scenarios.removed.map((s) => s.name).join(", ")}`);
  return parts.length ? parts.join(". ") + "." : "No changes.";
}

/** What stops a release: a step with nowhere to go, a step nothing leads to, a decision with one branch. */
export function problemsOf(m: Snapshot): { stepId?: string; text: string }[] {
  const out: { stepId?: string; text: string }[] = [];
  const reach = new Set<string>(), todo = [startId(m)].filter(Boolean) as string[];
  while (todo.length) { const id = todo.pop()!; if (reach.has(id)) continue; reach.add(id); for (const f of outs(m, id)) todo.push(f.to); }
  if (!m.steps.some((s) => s.type === "end")) out.push({ text: "The map has no end step." });
  if (m.steps.filter((s) => s.type === "start").length > 1) out.push({ text: "The map has more than one start step." });
  for (const s of m.steps) {
    const o = outs(m, s.id).length;
    if (s.type !== "end" && !o) out.push({ stepId: s.id, text: `${s.name} has no next step.` });
    else if (s.type === "decision" && o < 2) out.push({ stepId: s.id, text: `${s.name} is a decision with one branch.` });
    if (!reach.has(s.id)) out.push({ stepId: s.id, text: `Nothing leads to ${s.name}.` });
  }
  return out;
}

export type Area = { id: string; name: string; code: string };
/** The suite's areas (apps/web/src/suite.ts) and the code each puts in its document numbers: SOP-SC-001. */
export const AREAS: Area[] = [
  { id: "commercial", name: "CRM", code: "CR" }, { id: "marketing", name: "Marketing", code: "MK" }, { id: "engineering", name: "PLM", code: "EN" },
  { id: "quality", name: "Quality", code: "QA" }, { id: "supply", name: "Supply chain", code: "SC" }, { id: "production", name: "Production", code: "PR" },
  { id: "inventory", name: "Inventory", code: "IN" }, { id: "maintenance", name: "Maintenance", code: "MX" }, { id: "safety", name: "Safety and EHS", code: "EHS" },
  { id: "documents", name: "Documents", code: "DC" }, { id: "training", name: "Training", code: "TR" }, { id: "improve", name: "Improvement", code: "CI" },
  { id: "factory", name: "Implementation Factory", code: "IF" }, { id: "requests", name: "Requests and approvals", code: "RQ" }, { id: "people", name: "People and HR", code: "HR" },
  { id: "it", name: "IT", code: "IT" }, { id: "workplace", name: "Workplace", code: "WP" }, { id: "finance", name: "Finance", code: "FI" },
];
export const areaName = (id: string | null | undefined) => AREAS.find((a) => a.id === id)?.name ?? "";

/** WI-SC-014-03: the step's place in the flow, under the SOP's number. */
export function wiNumber(docNo: string, index: number) {
  return `WI-${docNo.replace(/^SOP-/i, "")}-${String(index).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------------------------------------------
// Reading order, and the steps people and agents follow
// ---------------------------------------------------------------------------------------------------------------
/**
 * Each step's column (how far along the flow it is, with loops ignored and one step per lane and column), the
 * handoffs that loop back, and the steps in reading order: by column, then by lane. The map is drawn in this order
 * and its steps are numbered by it (WI-SC-014-03), so the SOP, the editor and an agent's instructions agree.
 */
export function flowOrder(d: Snapshot): { rank: Record<string, number>; back: Set<string>; ordered: string[] } {
  const ids = d.steps.map((s) => s.id), idx: Record<string, number> = {}, li: Record<string, number> = {};
  ids.forEach((id, i) => (idx[id] = i));
  d.lanes.forEach((l, i) => (li[l.id] = i));
  const out: Record<string, string[]> = {};
  ids.forEach((id) => (out[id] = []));
  for (const f of d.flows) if (out[f.from] && idx[f.to] !== undefined && f.from !== f.to) out[f.from].push(f.to);

  // Handoffs that close a loop (rework) don't push steps to the right.
  const color: Record<string, number> = {}, back = new Set<string>();
  const dfs = (u: string) => {
    color[u] = 1;
    for (const v of out[u]) { if (color[v] === 1) back.add(`${u}>${v}`); else if (!color[v]) dfs(v); }
    color[u] = 2;
  };
  for (const r of [...d.steps.filter((s) => s.type === "start").map((s) => s.id), ...ids]) if (!color[r]) dfs(r);

  const fo: Record<string, string[]> = {}, indeg: Record<string, number> = {};
  ids.forEach((id) => { fo[id] = []; indeg[id] = 0; });
  for (const id of ids) for (const v of out[id]) { if (back.has(`${id}>${v}`)) continue; fo[id].push(v); indeg[v]++; }
  const q = ids.filter((id) => indeg[id] === 0).sort((a, b) => idx[a] - idx[b]), order: string[] = [];
  while (q.length) { const u = q.shift()!; order.push(u); for (const v of fo[u]) if (--indeg[v] === 0) q.push(v); }
  for (const id of ids) if (!order.includes(id)) order.push(id);

  const rank: Record<string, number> = {};
  ids.forEach((id) => (rank[id] = 0));
  const relax = () => { for (const u of order) for (const v of fo[u]) if (rank[v] < rank[u] + 1) rank[v] = rank[u] + 1; };
  relax();
  for (let it = 0; it < 300; it++) {
    const seen: Record<string, string> = {};
    let bumped = false;
    for (const u of order) {
      const k = `${li[d.steps[idx[u]].lane] ?? 0}|${rank[u]}`;
      if (seen[k]) { rank[u]++; bumped = true; } else seen[k] = u;
    }
    if (!bumped) break;
    relax();
  }
  const laneOf = (id: string) => li[d.steps[idx[id]].lane] ?? 0;
  // The start reads first and the end last, whatever column they sit in.
  const place = (id: string) => ({ start: 0, end: 2 } as Record<string, number>)[d.steps[idx[id]].type] ?? 1;
  const ordered = ids.slice().sort((a, b) => place(a) - place(b) || rank[a] - rank[b] || laneOf(a) - laneOf(b));
  return { rank, back, ordered };
}

export type DoneBy = "agent" | "person";
/** Who does a step: an agent when its lane is an agent lane, otherwise the person in that role. */
export const doneBy = (m: Pick<Snapshot, "lanes">, s: Pick<Step, "lane">): DoneBy => (m.lanes.find((l) => l.id === s.lane)?.agent ? "agent" : "person");

export type FollowStep = {
  step: number; wi: string; id: string; name: string; type: StepType; lane: string; doneBy: DoneBy;
  system?: string; screen?: string; instruction?: string; control?: string;
  next: { step: number; name: string; when?: string }[];
};
/**
 * A revision as the steps to follow, for a person or an agent: each step with its number, who does it, where and how,
 * and where it goes next (at a decision, which branch for which answer). For one scenario, only the steps on its path,
 * in the order they're done; otherwise every step in reading order, and each scenario as the step numbers on its path.
 */
export function stepsToFollow(m: Released, docNo: string, scenarioId?: string) {
  const B = byId(m), n: Record<string, number> = {};
  flowOrder(m).ordered.forEach((id, i) => (n[id] = i + 1));
  const sc = scenarioId ? m.scenarios.find((x) => x.id === scenarioId) : undefined;
  if (scenarioId && !sc) throw new MapError(`There's no scenario ${scenarioId} on this map.`);
  const path = sc ? pathOf(m, sc) : null;
  const ids = path ?? Object.keys(n).sort((a, b) => n[a] - n[b]);
  const laneName = (id: string) => m.lanes.find((l) => l.id === id)?.name ?? "";
  const steps: FollowStep[] = ids.map((id, i) => {
    const s = B[id];
    const next = path ? (path[i + 1] ? outs(m, id).filter((f) => f.to === path[i + 1]) : []) : outs(m, id);
    return {
      step: n[id], wi: wiNumber(docNo, n[id]), id, name: s.name, type: s.type, lane: laneName(s.lane), doneBy: doneBy(m, s),
      ...(s.type === "end" ? {} : { system: SYSTEM_LABEL[s.system] ?? "Manual" }),
      ...(s.screen ? { screen: s.screen } : {}), ...(s.instruction ? { instruction: s.instruction } : {}), ...(s.control ? { control: s.control } : {}),
      next: next.map((f) => ({ step: n[f.to], name: B[f.to]?.name ?? f.to, ...(f.label ? { when: f.label } : {}) })),
    };
  });
  return {
    lanes: m.lanes.map((l) => ({ name: l.name, doneBy: (l.agent ? "agent" : "person") as DoneBy })),
    scenario: sc ? { id: sc.id, name: sc.name, ...(sc.condition ? { startsWhen: sc.condition } : {}) } : undefined,
    scenarios: sc ? undefined : m.scenarios.map((x) => ({ id: x.id, name: x.name, ...(x.condition ? { startsWhen: x.condition } : {}), steps: pathOf(m, x).map((id) => n[id]) })),
    steps,
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Validation: what the API accepts as a map. Anything else is refused with a reason rather than stored.
// ---------------------------------------------------------------------------------------------------------------
export class MapError extends Error {}
/** A document number: letters and numbers in two to four parts, like SOP-SC-014. */
export const DOC_NO = /^[A-Z][A-Z0-9]{0,9}(-[A-Z0-9]{1,10}){1,3}$/;
const ID = /^[a-z0-9][a-z0-9-]{0,39}$/;
const LIMITS = { lanes: 30, steps: 300, flows: 600, scenarios: 40 };
const str = (v: unknown, max: number) => (typeof v === "string" ? v.trim().slice(0, max) : "");
const TYPES: StepType[] = ["start", "task", "decision", "end"];
const SYSTEMS: SystemKind[] = ["courage", "bc", "manual"];

function cleanLink(v: any): StepLink | null {
  if (!v || typeof v !== "object") return null;
  const url = str(v.url, 500);
  if (!/^https:\/\/[^\s"'<>]+$/.test(url)) return null;
  return { id: str(v.id, 40), label: str(v.label, 120), url };
}

/** A map as the API stores it. Ids must be short slugs; flows must join steps that exist; text is trimmed to size. */
export function sanitizeMap(input: unknown): MapBody {
  const b = input as any;
  if (!b || typeof b !== "object") throw new MapError("map must be an object");
  for (const k of ["lanes", "steps", "flows", "scenarios"] as const) if (!Array.isArray(b[k])) throw new MapError(`${k} must be a list`);
  if (b.lanes.length < 1) throw new MapError("a map needs at least one lane");
  for (const [k, max] of Object.entries(LIMITS)) if (b[k].length > max) throw new MapError(`a map has at most ${max} ${k}`);
  const seen = new Set<string>();
  const uniq = (id: unknown, what: string) => {
    if (typeof id !== "string" || !ID.test(id)) throw new MapError(`${what} id ${JSON.stringify(id)} is not a short lowercase slug`);
    if (seen.has(`${what}:${id}`)) throw new MapError(`${what} id ${id} is used twice`);
    seen.add(`${what}:${id}`); return id;
  };
  const lanes: Lane[] = b.lanes.map((l: any) => {
    const lane: Lane = { id: uniq(l?.id, "lane"), name: str(l?.name, 60) || "Lane" };
    if (l?.agent === true) lane.agent = true;
    return lane;
  });
  const laneIds = new Set(lanes.map((l) => l.id));
  const steps: Step[] = b.steps.map((s: any) => {
    const step: Step = {
      id: uniq(s?.id, "step"), name: str(s?.name, 120) || "Untitled step",
      type: TYPES.includes(s?.type) ? s.type : "task",
      lane: laneIds.has(s?.lane) ? s.lane : lanes[0].id,
      system: SYSTEMS.includes(s?.system) ? s.system : "manual",
      screen: str(s?.screen, 80), owner: str(s?.owner, 80),
      instruction: str(s?.instruction, 2000), kpi: str(s?.kpi, 120), control: str(s?.control, 160),
    };
    const link = cleanLink(s?.link); if (link) step.link = link;
    return step;
  });
  if (!steps.some((s) => s.type === "start")) throw new MapError("a map needs a start step");
  const stepIds = new Set(steps.map((s) => s.id));
  const flows: Flow[] = [];
  for (const f of b.flows) {
    if (!f || !stepIds.has(f.from) || !stepIds.has(f.to) || f.from === f.to) continue;
    if (flows.some((x) => x.from === f.from && x.to === f.to)) continue;
    flows.push({ from: f.from, to: f.to, label: str(f.label, 30) });
  }
  const scenarios: Scenario[] = b.scenarios.map((sc: any) => {
    const choices: Record<string, string> = {};
    if (sc?.choices && typeof sc.choices === "object")
      for (const [k, v] of Object.entries(sc.choices)) if (stepIds.has(k) && typeof v === "string" && stepIds.has(v)) choices[k] = v;
    return { id: uniq(sc?.id, "scenario"), name: str(sc?.name, 80) || "Scenario", choices, condition: str(sc?.condition, 300) };
  });
  return { lanes, steps, flows, scenarios };
}

/** The draft as a revision: a deep copy. */
export const releasedOf = (m: MapBody): Released => JSON.parse(JSON.stringify({ lanes: m.lanes, steps: m.steps, flows: m.flows, scenarios: m.scenarios }));

// ---------------------------------------------------------------------------------------------------------------
// Changes as operations: how Courage Chat and AI tools change a map without rewriting it.
// ---------------------------------------------------------------------------------------------------------------
export type Op =
  | { op: "add_lane"; id?: string; name: string; after?: string; agent?: boolean }
  | { op: "rename_lane"; id: string; name: string }
  | { op: "update_lane"; id: string; name?: string; agent?: boolean }
  | { op: "add_step"; id?: string; name: string; type?: "task" | "decision"; lane: string; system?: SystemKind; screen?: string; owner?: string; instruction?: string; kpi?: string; control?: string }
  | { op: "update_step"; id: string; fields: Partial<Pick<Step, "name" | "lane" | "system" | "screen" | "owner" | "instruction" | "kpi" | "control">> }
  | { op: "delete_step"; id: string }
  | { op: "add_flow"; from: string; to: string; label?: string }
  | { op: "remove_flow"; from: string; to: string }
  | { op: "add_scenario"; id?: string; name: string; choices?: Record<string, string>; condition?: string };

/** A short id from a name, made unique among `taken`: "Credit check" → credit-check, credit-check-2… */
export function slugId(name: string, taken: Set<string>) {
  const base = String(name ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 24) || "step";
  let id = base, n = 2;
  while (taken.has(id)) id = `${base}-${n++}`;
  return id;
}

/** Remove a step and join what led into it to what came after it. The start step stays. */
export function deleteStep(d: MapBody, id: string): boolean {
  const s = d.steps.find((x) => x.id === id);
  if (!s || s.type === "start") return false;
  const into = d.flows.filter((f) => f.to === id), next = d.flows.find((f) => f.from === id)?.to;
  d.flows = d.flows.filter((f) => f.from !== id && f.to !== id);
  d.steps = d.steps.filter((x) => x.id !== id);
  if (next) for (const f of into) if (f.from !== next && !d.flows.some((g) => g.from === f.from && g.to === next)) d.flows.push({ from: f.from, to: next, label: f.label });
  return true;
}

/**
 * Apply operations to a map in place. Each one that fits is applied; one that doesn't (an unknown step, a flow that
 * is already there) is skipped with the reason. New ids are made from names; later operations may refer to an
 * operation's own id for a new step or lane, and are pointed at the id it got. Scenarios keep their paths.
 */
export function applyOps(d: MapBody, input: unknown): { applied: number; added: string[]; skipped: string[] } {
  const prev: MapBody = JSON.parse(JSON.stringify(d));
  const ops: any[] = (Array.isArray(input) ? input : []).slice(0, 100).map((o) => (o && typeof o === "object" ? { ...o } : null));
  const out = { applied: 0, added: [] as string[], skipped: [] as string[] };
  const s = (v: unknown, n: number) => String(v ?? "").trim().slice(0, n);
  const repoint = (from: string, to: string, keys: string[]) => { for (const x of ops) if (x) for (const k of keys) if (x[k] === from) x[k] = to; };
  for (const o of ops) {
    if (!o) continue;
    const B = byId(d), lanes = new Set(d.lanes.map((l) => l.id));
    const skip = (why: string) => out.skipped.push(`${o.op}: ${why}`);
    switch (o.op) {
      case "add_lane": {
        const id = slugId(o.id || o.name, lanes), lane: Lane = { id, name: s(o.name, 60) || "New lane" };
        if (o.agent === true) lane.agent = true;
        const at = d.lanes.findIndex((l) => l.id === o.after);
        if (at >= 0) d.lanes.splice(at + 1, 0, lane); else d.lanes.push(lane);
        if (o.id && o.id !== id) repoint(o.id, id, ["lane", "after"]);
        out.applied++; break;
      }
      case "rename_lane": { const l = d.lanes.find((x) => x.id === o.id); if (!l || !s(o.name, 60)) { skip(`no lane ${o.id}`); break; } l.name = s(o.name, 60); out.applied++; break; }
      case "update_lane": {
        const l = d.lanes.find((x) => x.id === o.id); if (!l) { skip(`no lane ${o.id}`); break; }
        if (s(o.name, 60)) l.name = s(o.name, 60);
        if (o.agent === true) l.agent = true; else if (o.agent === false) delete l.agent;
        out.applied++; break;
      }
      case "add_step": {
        const id = slugId(o.id || o.name, new Set(Object.keys(B)));
        if (o.id && o.id !== id) repoint(o.id, id, ["from", "to", "id"]);
        d.steps.push({
          id, name: s(o.name, 120) || "New step", type: o.type === "decision" ? "decision" : "task", lane: lanes.has(o.lane) ? o.lane : d.lanes[0].id,
          system: SYSTEMS.includes(o.system) ? o.system : "manual", screen: s(o.screen, 80), owner: s(o.owner, 80),
          instruction: s(o.instruction, 2000), kpi: s(o.kpi, 120), control: s(o.control, 160),
        });
        out.added.push(id); out.applied++; break;
      }
      case "update_step": {
        const st = d.steps.find((x) => x.id === o.id), f = o.fields && typeof o.fields === "object" ? o.fields : null;
        if (!st || !f) { skip(`no step ${o.id}`); break; }
        for (const [k, max] of [["name", 120], ["screen", 80], ["owner", 80], ["instruction", 2000], ["kpi", 120], ["control", 160]] as const)
          if (typeof f[k] === "string") (st as any)[k] = s(f[k], max);
        if (lanes.has(f.lane)) st.lane = f.lane;
        if (SYSTEMS.includes(f.system)) st.system = f.system;
        out.applied++; break;
      }
      case "delete_step": if (deleteStep(d, o.id)) out.applied++; else skip(`can't delete ${o.id}`); break;
      case "add_flow": {
        if (!B[o.from] || !B[o.to] || o.from === o.to) { skip(`no step ${B[o.from] ? o.to : o.from}`); break; }
        if (B[o.from].type === "end") { skip("the end step has no next step"); break; }
        if (d.flows.some((f) => f.from === o.from && f.to === o.to)) { skip(`${o.from} already goes to ${o.to}`); break; }
        d.flows.push({ from: o.from, to: o.to, label: s(o.label, 30) }); out.applied++; break;
      }
      case "remove_flow": {
        const n = d.flows.length; d.flows = d.flows.filter((f) => !(f.from === o.from && f.to === o.to));
        if (d.flows.length < n) out.applied++; else skip(`${o.from} doesn't go to ${o.to}`); break;
      }
      case "add_scenario": {
        const id = slugId(o.id || o.name, new Set(d.scenarios.map((x) => x.id)));
        const choices: Record<string, string> = {};
        if (o.choices && typeof o.choices === "object") for (const [k, v] of Object.entries(o.choices)) if (B[k] && typeof v === "string" && B[v]) choices[k] = v;
        d.scenarios.push({ id, name: s(o.name, 80) || "Scenario", choices, condition: s(o.condition, 300) });
        out.applied++; break;
      }
      default: skip("unknown operation");
    }
  }
  fixChoices(prev, d);
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Starter maps: a blank map, the order-to-ship example, and the template library (templates.ts)
// ---------------------------------------------------------------------------------------------------------------
export type TemplateInfo = { id: string; name: string; area: string; summary: string; owner?: string; lanes?: number; steps?: number; scenarios?: number; agent?: boolean };
const BASICS = [
  { id: "blank", name: "Blank map", area: "", summary: "One lane, a start, one step and an end." },
  { id: "order-to-ship", name: "Order to ship", area: "supply", owner: "Supply chain manager", summary: "From the customer PO to the invoice, with an agent entering the sales order: stock, make-to-order and rush scenarios." },
];
function templateInfos(): TemplateInfo[] {
  const size = (m: MapBody) => ({ lanes: m.lanes.length, steps: m.steps.length, scenarios: m.scenarios.length, agent: m.lanes.some((l) => l.agent) });
  return [
    ...BASICS.map((b) => (b.id === "blank" ? b : { ...b, ...size(templateMap(b.id)) })),
    ...LIBRARY.map((t) => ({ id: t.id, name: t.name, area: t.area, owner: t.owner, summary: t.summary, ...size(buildTemplate(t)) })),
  ];
}
/** Every starting point, with its size, for the new map form and AI tools. */
export const TEMPLATES: TemplateInfo[] = /* @__PURE__ */ templateInfos();

export function templateMap(id: string): MapBody {
  const lib = LIBRARY.find((t) => t.id === id);
  if (lib) return buildTemplate(lib);
  if (id !== "order-to-ship") return {
    lanes: [{ id: "lane-1", name: "Lane 1" }],
    steps: [
      step("start", "Start", "start", "lane-1", "manual", "", ""),
      step("step-1", "First step", "task", "lane-1", "manual", "", ""),
      step("end", "Done", "end", "lane-1", "manual", "", ""),
    ],
    flows: [{ from: "start", to: "step-1", label: "" }, { from: "step-1", to: "end", label: "" }],
    scenarios: [],
  };
  const steps: Step[] = [
    step("start", "Customer PO received", "start", "cs", "manual", "Email or EDI", "Customer service rep", "The customer PO arrives in the order inbox by email or EDI."),
    step("enter", "Enter sales order", "task", "agent", "bc", "Sales Order", "", "Create the sales order from the customer PO: the customer, the customer PO number, the requested ship date and each line. Flag any line whose item or price doesn't match the quote.", "Entered the day the PO arrives", "Customer PO number required"),
    step("release", "Release sales order", "task", "cs", "bc", "Sales Order", "Customer service rep", "Check the prices, the ship-to address and any line the agent flagged. Confirm the requested ship date with the customer, then choose Release.", "", "Price change over 5% needs approval"),
    step("avail", "Available to ship?", "decision", "plan", "courage", "Planning worksheet", "Planner", "Check projected available inventory on the requested ship date."),
    step("prodord", "Create production order", "task", "plan", "bc", "Firm Planned Prod. Order", "Planner", "Create a firm planned production order from the sales line. Refresh the components and the routing.", "", "Order linked to the sales line"),
    step("rush", "Rush order?", "decision", "plan", "courage", "Planning worksheet", "Planner", "An order is a rush order when the requested date is earlier than the standard lead time."),
    step("expedite", "Expedite approval", "task", "plan", "courage", "Approvals", "Operations manager", "Approve overtime or a schedule change so the order meets the requested date. Record the reason in the approval.", "Approval within 4 hours", "Approval recorded before release to the floor"),
    step("build", "Build to order", "task", "prod", "courage", "Shop floor", "Production lead", "Start each operation on the shop floor terminal. Report output and scrap, then finish the order.", "On-time completion"),
    step("pick", "Pick order", "task", "wh", "courage", "Warehouse picks", "Warehouse lead", "Pick from the bins shown. Scan each item and register the pick.", "Pick accuracy", "Scan required on each line"),
    step("ship", "Ship and post", "task", "wh", "bc", "Warehouse Shipment", "Shipping clerk", "Print the packing list, book the carrier and post the shipment.", "On-time shipment"),
    step("invoice", "Post invoice", "task", "fin", "bc", "Sales Invoice", "AR specialist", "Post the invoice from the posted shipment within one business day.", "Days to invoice"),
    step("end", "Order complete", "end", "fin", "manual", "", "AR specialist", ""),
  ];
  const F = (from: string, to: string, label = ""): Flow => ({ from, to, label });
  return {
    lanes: [{ id: "cs", name: "Customer service" }, { id: "agent", name: "Order entry agent", agent: true }, { id: "plan", name: "Planning" }, { id: "prod", name: "Production" }, { id: "wh", name: "Warehouse" }, { id: "fin", name: "Finance" }],
    steps,
    flows: [F("start", "enter"), F("enter", "release"), F("release", "avail"), F("avail", "pick", "Yes"), F("avail", "prodord", "No"), F("prodord", "rush"),
      F("rush", "expedite", "Yes"), F("rush", "build", "No"), F("expedite", "build"), F("build", "pick"), F("pick", "ship"), F("ship", "invoice"), F("invoice", "end")],
    scenarios: [
      { id: "stock", name: "Stock order", choices: { avail: "pick" }, condition: "Stock is available on the requested ship date." },
      { id: "mto", name: "Make to order", choices: { avail: "prodord", rush: "build" }, condition: "Stock is short and the requested date allows the standard lead time." },
      { id: "rush", name: "Rush order", choices: { avail: "prodord", rush: "expedite" }, condition: "Stock is short and the requested date is inside the standard lead time." },
    ],
  };
}

/**
 * The order-to-ship map before an agent took over order entry: customer service entered the order by hand. The
 * example's Rev A, in the app's preview data and the website demo; the template is its Rev B.
 */
export function orderToShipByHand(): MapBody {
  const m = templateMap("order-to-ship");
  m.lanes = m.lanes.filter((l) => !l.agent);
  Object.assign(m.steps.find((x) => x.id === "enter")!, {
    lane: "cs", owner: "Customer service rep", kpi: "Order entry under 10 minutes",
    instruction: "Open Sales Orders and choose New. Enter the customer, the customer PO number, the requested ship date and each line.",
  });
  m.steps.find((x) => x.id === "release")!.instruction = "Check the prices and the ship-to address. Confirm the requested ship date with the customer, then choose Release.";
  return m;
}

function step(id: string, name: string, type: StepType, lane: string, system: SystemKind, screen: string, owner: string, instruction = "", kpi = "", control = ""): Step {
  return { id, name, type, lane, system, screen, owner, instruction, kpi, control };
}
